// Store
import { RootState } from "app/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSearchInfo } from "store/slice/actions";
import { customAsyncThunk } from "store/slice/slices.functions";

// Utils
import { differenceBy } from "lodash";

// Services
import { fetchProjectByIdService } from "services/projects/project.service";

// Types
import { RequestInfo } from "store/slice/store.types";
import {
    ProjectDataChildSearchAIList,
    ProjectSearchAICreationResponse,
    searchAIAllChildren,
    searchAIChildCompanyAB,
    searchAIChildSearchCompanies,
    SearchAIChildTypes,
} from "services/projects/project.types";

type TSearchAisSlice = {
    status: RequestInfo;
    data: ProjectSearchAICreationResponse[];
};

const initialState: TSearchAisSlice = {
    status: "pristine",
    data: [] as ProjectSearchAICreationResponse[],
};

export const fetchProjectSearchAi = customAsyncThunk<
    ProjectSearchAICreationResponse,
    { projectId: number | string; path: string }
>("project/serachAis/getSearchAi", async ({ projectId, path }, { getState }) => {
    const {
        project: {
            searchAis: { data: searchAi },
        },
    } = getState();

    const searchAiData = searchAi.find((searchAi) => searchAi.meta.path === path);

    if (searchAiData) {
        return searchAiData;
    } else {
        const { data } = await fetchProjectByIdService<ProjectSearchAICreationResponse>(Number(projectId), path);
        return data;
    }
});

export const fetchAllProjectSearchAis = customAsyncThunk<
    ProjectDataChildSearchAIList,
    { projectId: number | string; path: string }
>("project/serachAis/getAllSearchAis", async ({ projectId, path }) => {
    const { data } = await fetchProjectByIdService<ProjectDataChildSearchAIList>(Number(projectId), path);
    return data;
});

const searchAisSlice = createSlice({
    name: "project/searchAis",
    initialState,
    reducers: {
        addSearchAisData: (state, action: PayloadAction<ProjectSearchAICreationResponse[] | undefined>) => {
            const existingSearchAis = state.data;
            const newSearchAis = action.payload || [];
            const newValues = differenceBy(newSearchAis, existingSearchAis, "data.id");
            return { ...state, data: [...state.data, ...newValues] };
        },
        updateSearchAiChild: (
            state,
            action: PayloadAction<{
                data: searchAIAllChildren;
                id: number;
                type: SearchAIChildTypes;
            }>
        ) => {
            const { data, id, type } = action.payload;
            const searchAI = state.data.find((searchAI) => searchAI.data.id === id);
            if (searchAI) {
                const searchAIChildren = searchAI.children.map((searchAIChild) => {
                    return searchAIChild.meta.type === type ? data : searchAIChild;
                });
                const updatedsearchAI = { ...searchAI, children: searchAIChildren };
                const searchAIs = state.data.map((searchAI) => {
                    return searchAI.data.id === id ? updatedsearchAI : searchAI;
                });

                return { ...state, data: searchAIs };
            }
            return state;
        },
        updateSearchAiCompanyChild: (
            state,
            action: PayloadAction<{
                data: searchAIChildCompanyAB;
                id: number;
                type: "PROJECT_COMPANY_SET_REF_A" | "PROJECT_COMPANY_SET_REF_B";
            }>
        ) => {
            // PROJECT_SEARCH_COMPANIES
            const { data, id, type } = action.payload;
            const searchAI = state.data.find((searchAI) => searchAI.data.id === id);
            if (searchAI) {
                const searchAIChildren = searchAI.children.map((searchAIChild) => {
                    if (searchAIChild.meta.type === "PROJECT_SEARCH_COMPANIES") {
                        const newChildren = (searchAIChild as searchAIChildSearchCompanies)?.children?.map(
                            (filterChild) => {
                                return filterChild.meta.type === type ? data : filterChild;
                            }
                        );

                        return { ...searchAIChild, children: newChildren };
                    } else {
                        return searchAIChild;
                    }
                });

                const updatedsearchAI = { ...searchAI, children: searchAIChildren };
                const searchAIs = state.data.map((searchAI) => {
                    return searchAI.data.id === id ? updatedsearchAI : searchAI;
                });

                return { ...state, data: searchAIs };
            }
            return state;
        },
        clearSearchAisData: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchProjectSearchAi.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(fetchProjectSearchAi.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(fetchProjectSearchAi.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : [...state.data, action.payload],
            status: "done",
        }));
        builder.addCase(fetchAllProjectSearchAis.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(fetchAllProjectSearchAis.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(fetchAllProjectSearchAis.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : action.payload.children,
            status: "done",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

export const { addSearchAisData, updateSearchAiChild, updateSearchAiCompanyChild, clearSearchAisData } =
    searchAisSlice.actions;

export const getSearchAis = (store: RootState) => store.project.searchAis;
export const getSearchAiById = (searchAiId: number) => (store: RootState) => ({
    data: store.project.searchAis.data.find((searchAi) => searchAi.data.id === Number(searchAiId)),
    status: store.project.searchAis.status,
});

export default searchAisSlice.reducer;
